/**
 * TODAY — the on-the-road surface (P3.md): now/next for the nearest gig day.
 * One glance answers the three questions of a show day: what's next, how do
 * we move, where do we sleep.
 *
 * Time discipline (ADR-006): stored times are venue-local wall times and are
 * displayed VERBATIM. "Now" is computed as the wall clock of the gig's IANA
 * timezone (gig header `timezone`, falling back to the device zone) — the
 * only conversion in this file, done with Intl, no libraries, no network.
 */
import { registry } from "@td/registry";
import { isItemDeleted, type Item } from "@td/sync-core";
import type { GigRecord } from "../db/idb.ts";
import { buildDayRunEntries, type DayRunEntry } from "./dayRun.ts";

function s(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** Wall-clock date (YYYY-MM-DD) + time (HH:MM) of `nowMs` in an IANA zone. */
export function wallClock(nowMs: number, tz: string): { day: string; time: string } {
  try {
    const dtf = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const parts: Record<string, string> = {};
    for (const p of dtf.formatToParts(new Date(nowMs))) parts[p.type] = p.value;
    return {
      day: `${parts["year"]}-${parts["month"]}-${parts["day"]}`,
      time: `${String(Number(parts["hour"]) % 24).padStart(2, "0")}:${parts["minute"]}`,
    };
  } catch {
    const d = new Date(nowMs);
    const pad = (n: number) => String(n).padStart(2, "0");
    return {
      day: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    };
  }
}

function gigTz(gig: GigRecord): string {
  const tz = s(gig.state.gig?.fields["timezone"]);
  if (tz !== "") return tz;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

function daysApart(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number) as [number, number, number];
  const [by, bm, bd] = b.split("-").map(Number) as [number, number, number];
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

export interface TodayFocus {
  gig: GigRecord;
  tz: string;
  day: string;
  isToday: boolean;
  /** Next upcoming entry of the day (schedule preferred), or the first one. */
  next: DayRunEntry | null;
  /** Travel legs departing that day. */
  travel: DayRunEntry[];
  /** Where the party sleeps that night. */
  hotel: Item | null;
}

/** Pick the nearest gig day across all gigs (today > soonest future > most
 * recent past) and assemble its now/next data. Null = nothing dated anywhere. */
export function pickTodayFocus(gigs: readonly GigRecord[], nowMs: number): TodayFocus | null {
  let best: { focus: TodayFocus; score: number } | null = null;

  for (const gig of gigs) {
    const tz = gigTz(gig);
    const now = wallClock(nowMs, tz);
    const entries = buildDayRunEntries(gig).filter((e) => DAY_RE.test(e.day));
    if (entries.length === 0) continue;

    const days = [...new Set(entries.map((e) => e.day))];
    let day = days.find((d) => d === now.day) ?? null;
    if (day === null) {
      const future = days.filter((d) => d > now.day).sort();
      const past = days.filter((d) => d < now.day).sort();
      day = future[0] ?? past[past.length - 1] ?? null;
    }
    if (day === null) continue;

    const isToday = day === now.day;
    const dist = Math.abs(daysApart(now.day, day));
    // today beats everything; then soonest future; past only as a last resort.
    const score = isToday ? 0 : day > now.day ? 1000 + dist : 1_000_000 + dist;
    if (best !== null && score >= best.score) continue;

    const dayEntries = entries.filter((e) => e.day === day);
    const upcoming = isToday ? dayEntries.filter((e) => e.time !== "" && e.time >= now.time) : dayEntries;
    const next =
      upcoming.find((e) => e.collection === registry.schedule.key) ??
      upcoming[0] ??
      (isToday ? null : (dayEntries[0] ?? null));

    const travel = dayEntries.filter((e) => e.collection === registry.travel.key);

    const hotelItems = (Object.values(gig.state.collections[registry.hotels.key] ?? {}) as Item[]).filter(
      (i) => !isItemDeleted(i),
    );
    const checkInKey = registry.hotels.fields.check_in.key;
    const hotel =
      hotelItems.find((h) => s(h.fields[checkInKey]).startsWith(day)) ??
      (hotelItems.length === 1 ? hotelItems[0]! : null);

    best = { focus: { gig, tz, day, isToday, next, travel, hotel }, score };
  }

  return best?.focus ?? null;
}

/** Absolute epoch ms of a venue-local YYYY-MM-DD/HH:MM instant in `tz`,
 * computed via the wall-clock offset so DST-adjacent dates stay correct
 * (ADR-006 discipline: no external tz math libraries). null if malformed.
 * Exported so the native glance can carry an absolute `primaryTime`. */
export function instantMs(tz: string, day: string, time: string): number | null {
  const m = /^([0-2]?\d):([0-5]\d)$/.exec(time);
  if (!DAY_RE.test(day) || m === null) return null;
  const [y, mo, d] = day.split("-").map(Number) as [number, number, number];
  const targetAsUtc = Date.UTC(y, mo - 1, d, Number(m[1]), Number(m[2]));
  // Fixed-point: the tz offset at a first guess can differ from the offset at
  // the TRUE instant across a DST jump (a single probe is off by ±1h on
  // transition mornings). Re-derive the offset at each candidate until it stops
  // moving — 2–3 iterations converge for standard transitions (ADR-006).
  let candidate = targetAsUtc;
  for (let i = 0; i < 4; i++) {
    const probe = wallClock(candidate, tz);
    const [py, pmo, pd] = probe.day.split("-").map(Number) as [number, number, number];
    const [ph, pmi] = probe.time.split(":").map(Number) as [number, number];
    const offset = Date.UTC(py, pmo - 1, pd, ph, pmi) - candidate;
    const next = targetAsUtc - offset;
    if (next === candidate) break;
    candidate = next;
  }
  return candidate;
}

/** Minutes from `nowMs` to a venue-local instant. Whole minutes, can be
 * negative (already departed). */
function minutesUntil(nowMs: number, tz: string, day: string, time: string): number | null {
  const ms = instantMs(tz, day, time);
  return ms === null ? null : Math.round((ms - nowMs) / 60_000);
}

export interface VerdictResult {
  key: "today.verdict.now" | "today.verdict.leave" | "today.verdict.today";
  minutes: number;
  /** For "leave": the formatted {t} value, split so the number can render mono. */
  leaveText?: string;
}

/** The verdict line rules (see task spec): null = render nothing (future day
 * or no next departure). */
export function computeVerdict(
  nowMs: number,
  tz: string,
  now: { day: string },
  next: DayRunEntry | null,
): VerdictResult | null {
  if (next === null || next.day === "" || next.day > now.day) return null;
  const m = minutesUntil(nowMs, tz, next.day, next.time);
  if (m === null) return null;
  if (m < 0) return { key: "today.verdict.now", minutes: m };
  if (m <= 120) {
    const leaveText = m < 60 ? `${m} min` : `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
    return { key: "today.verdict.leave", minutes: m, leaveText };
  }
  if (next.day === now.day) return { key: "today.verdict.today", minutes: m };
  return null;
}

